import 'react-native-url-polyfill/auto';

import { createClient } from '@supabase/supabase-js';

type StorageAdapter = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
};

class MemoryStorage implements StorageAdapter {
  private store = new Map<string, string>();

  async getItem(key: string) {
    return this.store.has(key) ? this.store.get(key)! : null;
  }

  async setItem(key: string, value: string) {
    this.store.set(key, value);
  }

  async removeItem(key: string) {
    this.store.delete(key);
  }
}

class SecureStoreAdapter implements StorageAdapter {
  private secureStore: typeof import('expo-secure-store');

  constructor(secureStore: typeof import('expo-secure-store')) {
    this.secureStore = secureStore;
  }

  async getItem(key: string) {
    return this.secureStore.getItemAsync(key);
  }

  async setItem(key: string, value: string) {
    await this.secureStore.setItemAsync(key, value);
  }

  async removeItem(key: string) {
    await this.secureStore.deleteItemAsync(key);
  }
}

const isReactNative = () =>
  typeof navigator !== 'undefined' && navigator.product === 'ReactNative';

const createStorageAdapter = (): StorageAdapter | undefined => {
  if (isReactNative()) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const SecureStore = require('expo-secure-store') as typeof import('expo-secure-store');
    return new SecureStoreAdapter(SecureStore);
  }

  if (typeof window === 'undefined') {
    return new MemoryStorage();
  }

  return undefined;
};

const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl) {
  throw new Error('Missing `EXPO_PUBLIC_SUPABASE_URL` environment variable.');
}

if (!supabaseAnonKey) {
  throw new Error('Missing `EXPO_PUBLIC_SUPABASE_ANON_KEY` environment variable.');
}

const storageAdapter = createStorageAdapter();
const isServerEnvironment = typeof window === 'undefined' && !isReactNative();

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: storageAdapter,
    autoRefreshToken: !isServerEnvironment,
    persistSession: !isServerEnvironment,
    detectSessionInUrl: false,
    flowType: 'pkce',
  },
});
